"use client";

import NextLink from "next/link";
import { Flex, Box, Link as ChakraLink } from "@chakra-ui/react";
import {
  buildAuthRouteHref,
  normalizeSafeInternalTarget,
} from "./authRedirectUtils";
import authSettings from "@/shared/config/authSettings";
import { authPageContent } from "./content";

type AuthTabsProps = {
  active: "register" | "login";
  targetUrl?: string;
};

export default function AuthTabs({ active, targetUrl }: AuthTabsProps) {
  const safeTargetUrl = normalizeSafeInternalTarget(targetUrl);

  const baseTabProps = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    py: "3",
    px: "3",
    fontSize: "md",
    rounded: "md",
    transition: "background-color 120ms ease, color 120ms ease",
  };

  const ACTIVE_TAB_BG = "moveon.public.surface.base";
  const ACTIVE_TAB_COLOR = "moveon.publicFont";
  const ACTIVE_TAB_BORDER = "moveon.public.surface.accentStrong";
  const INACTIVE_TAB_BG = "transparent";
  const INACTIVE_TAB_COLOR = "moveon.publicFont";
  const INACTIVE_TAB_HOVER_BG = "moveon.public.surface.accent";
  const registerHref = buildAuthRouteHref(
    authSettings.redirects.register,
    safeTargetUrl,
  );
  const loginHref = buildAuthRouteHref(
    authSettings.redirects.login,
    safeTargetUrl,
  );

  return (
    <Flex
      bg="moveon.public.surface.muted"
      borderBottomWidth="1"
      borderBottomColor="moveon.public.surface.border"
      gap="1"
      p="1"
    >
      {active === "register" ? (
        <Box
          {...baseTabProps}
          bg={ACTIVE_TAB_BG}
          color={ACTIVE_TAB_COLOR}
          fontWeight="bold"
          shadow="sm"
          aria-current="page"
          borderWidth="1"
          borderColor={ACTIVE_TAB_BORDER}
        >
          {authPageContent.tabs.register}
        </Box>
      ) : (
        <ChakraLink
          asChild
          {...baseTabProps}
          bg={INACTIVE_TAB_BG}
          color={INACTIVE_TAB_COLOR}
          fontWeight="medium"
          _hover={{
            textDecoration: "none",
            bg: INACTIVE_TAB_HOVER_BG,
            color: "moveon.publicFont",
          }}
          _focusVisible={{
            outlineWidth: "2",
            outlineStyle: "solid",
            outlineColor: ACTIVE_TAB_BORDER,
            outlineOffset: "2px",
          }}
        >
          <NextLink href={registerHref}>
            {authPageContent.tabs.register}
          </NextLink>
        </ChakraLink>
      )}

      {active === "login" ? (
        <Box
          {...baseTabProps}
          bg={ACTIVE_TAB_BG}
          color={ACTIVE_TAB_COLOR}
          fontWeight="bold"
          shadow="sm"
          aria-current="page"
          borderWidth="1"
          borderColor={ACTIVE_TAB_BORDER}
        >
          {authPageContent.tabs.login}
        </Box>
      ) : (
        <ChakraLink
          asChild
          {...baseTabProps}
          bg={INACTIVE_TAB_BG}
          color={INACTIVE_TAB_COLOR}
          fontWeight="medium"
          _hover={{
            textDecoration: "none",
            bg: INACTIVE_TAB_HOVER_BG,
            color: "moveon.publicFont",
          }}
          _focusVisible={{
            outlineWidth: "2",
            outlineStyle: "solid",
            outlineColor: ACTIVE_TAB_BORDER,
            outlineOffset: "2px",
          }}
        >
          <NextLink href={loginHref}>{authPageContent.tabs.login}</NextLink>
        </ChakraLink>
      )}
    </Flex>
  );
}
